<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { splitName, debounce } from '@chatwoot/utils';
import { useCompaniesStore } from 'dashboard/stores/companies';
import countries from 'shared/constants/countries.js';
import ContactAPI from 'dashboard/api/contacts';
import { useMapGetter } from 'dashboard/composables/store';
import { useFieldOrder } from 'dashboard/composables/useFieldOrder';
import { PIXEL_ADAPTED_CONTACT_FIELDS } from 'dashboard/components-next/Forms/standardFields';
import { useLocationOptions } from 'dashboard/composables/useLocationOptions';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import FormSectionHeader from 'dashboard/components-next/Forms/FormSectionHeader.vue';
import ContactCustomAttributeItem from 'dashboard/components-next/Contacts/ContactsSidebar/ContactCustomAttributeItem.vue';
import FormFieldLabel from 'dashboard/components-next/Forms/FormFieldLabel.vue';
import ContactFormLabels from 'dashboard/components-next/Contacts/ContactsForm/ContactFormLabels.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  contactData: {
    type: Object,
    default: null,
  },
  isDetailsView: {
    type: Boolean,
    default: false,
  },
  isNewContact: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();
const uiFlags = useMapGetter('contacts/getUIFlags');

const FORM_CONFIG = {
  FIRST_NAME: { field: 'firstName' },
  LAST_NAME: { field: 'lastName' },
  EMAIL_ADDRESS: { field: 'email' },
  PHONE_NUMBER: { field: 'phoneNumber' },
  BIRTH_DATE: { field: 'additionalAttributes.birthDate' },
  ZIP_CODE: { field: 'additionalAttributes.zipCode' },
  CITY: { field: 'additionalAttributes.city' },
  STATE: { field: 'additionalAttributes.state' },
  COUNTRY: { field: 'additionalAttributes.countryCode' },
  BIO: { field: 'additionalAttributes.description' },
};

const STANDARD_TO_CONFIG = {
  first_name: 'FIRST_NAME',
  last_name: 'LAST_NAME',
  email: 'EMAIL_ADDRESS',
  phone_number: 'PHONE_NUMBER',
  birth_date: 'BIRTH_DATE',
  gender: 'GENDER',
  zip_code: 'ZIP_CODE',
  city: 'CITY',
  state: 'STATE',
  country: 'COUNTRY',
  bio: 'BIO',
};

const GENDER_VALUES = ['male', 'female', 'other'];

const SOCIAL_CONFIG = {
  LINKEDIN: 'i-ri-linkedin-box-fill',
  FACEBOOK: 'i-ri-facebook-circle-fill',
  INSTAGRAM: 'i-ri-instagram-line',
  TELEGRAM: 'i-ri-telegram-fill',
  TIKTOK: 'i-ri-tiktok-fill',
  TWITTER: 'i-ri-twitter-x-fill',
  GITHUB: 'i-ri-github-fill',
};

const defaultState = {
  id: 0,
  name: '',
  email: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  companyId: null,
  customAttributes: {},
  additionalAttributes: {
    description: '',
    companyName: '',
    countryCode: '',
    country: '',
    city: '',
    state: '',
    zipCode: '',
    birthDate: '',
    gender: '',
    socialProfiles: {
      facebook: '',
      github: '',
      instagram: '',
      telegram: '',
      tiktok: '',
      linkedin: '',
      twitter: '',
    },
  },
};

const state = reactive({ ...defaultState });
const selectedLabels = ref([]);

const validationRules = {
  firstName: { required },
  email: { email },
};

const v$ = useVuelidate(validationRules, state);

const isFormInvalid = computed(() => v$.value.$invalid);

const { orderedFields } = useFieldOrder('contact');

const prepareStateBasedOnProps = () => {
  if (props.isNewContact) {
    return; // Added to prevent state update for new contact form
  }

  const {
    id,
    name = '',
    email: emailAddress,
    phoneNumber = '',
    companyId = null,
    additionalAttributes = {},
    customAttributes = {},
  } = props.contactData || {};
  const { firstName, lastName } = splitName(name || '');
  const {
    description = '',
    companyName = '',
    countryCode = '',
    country = '',
    city = '',
    state: addressState = '',
    zipCode = '',
    birthDate = '',
    gender = '',
    socialTelegramUserName = '',
    socialProfiles = {},
  } = additionalAttributes || {};

  const telegramUsername =
    socialProfiles?.telegram || socialTelegramUserName || '';

  Object.assign(state, {
    id,
    name,
    firstName,
    lastName,
    email: emailAddress,
    phoneNumber,
    companyId,
    customAttributes: { ...customAttributes },
    additionalAttributes: {
      description,
      companyName,
      countryCode,
      country,
      city,
      state: addressState,
      zipCode,
      birthDate,
      gender,
      socialProfiles: {
        ...socialProfiles,
        telegram: telegramUsername,
      },
    },
  });
};

const countryOptions = computed(() =>
  countries.map(({ name, id }) => ({ label: name, value: id }))
);

const { stateOptions, cityOptions, loadingStates, loadingCities } =
  useLocationOptions(
    () => state.additionalAttributes.countryCode,
    () => state.additionalAttributes.state
  );

// Um valor já salvo que não está na lista (texto digitado antes dos selects) continua aparecendo.
const withCurrent = (options, value) =>
  value && !options.some(option => option.value === value)
    ? [...options, { label: value, value }]
    : options;

const stateSelectOptions = computed(() =>
  withCurrent(stateOptions.value, state.additionalAttributes.state)
);
const citySelectOptions = computed(() =>
  withCurrent(cityOptions.value, state.additionalAttributes.city)
);

// Estado e cidade são selects enquanto houver (ou estiver carregando) uma lista; sem país escolhido
// ficam desabilitados, e um país sem dados volta para o texto livre.
const isStateSelect = computed(
  () =>
    !state.additionalAttributes.countryCode ||
    loadingStates.value ||
    stateOptions.value.length > 0
);
const isCitySelect = computed(
  () =>
    !state.additionalAttributes.state ||
    loadingCities.value ||
    cityOptions.value.length > 0
);

const statePlaceholder = computed(() => {
  if (!state.additionalAttributes.countryCode) {
    return t(
      'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.STATE.SELECT_COUNTRY'
    );
  }
  return loadingStates.value
    ? t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.LOADING')
    : t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.STATE.SELECT');
});
const cityPlaceholder = computed(() => {
  if (!state.additionalAttributes.state) {
    return t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.CITY.SELECT_STATE');
  }
  return loadingCities.value
    ? t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.LOADING')
    : t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.CITY.SELECT');
});

const genderOptions = computed(() =>
  GENDER_VALUES.map(value => ({
    label: t(
      `CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.GENDER.OPTIONS.${value}`
    ),
    value,
  }))
);

const socialProfilesForm = computed(() =>
  Object.entries(SOCIAL_CONFIG).map(([key, icon]) => ({
    key,
    placeholder: t(`CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.FORM.${key}.PLACEHOLDER`),
    icon,
  }))
);

const hasPixelHint = key => PIXEL_ADAPTED_CONTACT_FIELDS.includes(key);

const standardLabel = key => {
  const configKey = STANDARD_TO_CONFIG[key];
  return configKey
    ? t(`CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.${configKey}.LABEL`)
    : '';
};

const standardPlaceholder = key => {
  const configKey = STANDARD_TO_CONFIG[key];
  return configKey
    ? t(`CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.${configKey}.PLACEHOLDER`)
    : '';
};

const customAttributeWithValue = attribute => ({
  ...attribute,
  value: state.customAttributes?.[attribute.attributeKey] ?? '',
});

const isValidationField = key => {
  const field = FORM_CONFIG[key]?.field;
  return ['firstName', 'email'].includes(field);
};

const getValidationKey = key => {
  return FORM_CONFIG[key]?.field;
};

// Creates a computed property for two-way form field binding
const getFormBinding = key => {
  const field = FORM_CONFIG[key]?.field;
  if (!field) return null;

  return computed({
    get: () => {
      // Handle firstName/lastName fields
      if (field === 'firstName' || field === 'lastName') {
        return state[field]?.toString() || '';
      }

      // Handle nested vs non-nested fields
      const [base, nested] = field.split('.');
      // Example: 'email' → state.email
      // Example: 'additionalAttributes.city' → state.additionalAttributes.city
      return (nested ? state[base][nested] : state[base])?.toString() || '';
    },

    set: value => {
      // Handle name fields specially to maintain the combined 'name' field
      if (field === 'firstName' || field === 'lastName') {
        state[field] = value;
        // Example: firstName="John", lastName="Doe" → name="John Doe"
        state.name = `${state.firstName} ${state.lastName}`.trim();
      } else {
        // Handle nested vs non-nested fields
        const [base, nested] = field.split('.');
        if (nested) {
          // Example: additionalAttributes.city = "New York"
          state[base][nested] = value;
        } else {
          // Example: email = "test@example.com"
          state[base] = value;
        }
      }

      const { firstName, lastName, ...stateWithoutNames } = state;
      emit('update', stateWithoutNames);
    },
  });
};

const getMessageType = key => {
  return isValidationField(key) && v$.value[getValidationKey(key)]?.$error
    ? 'error'
    : 'info';
};

const getErrorMessage = key => {
  const field = getValidationKey(key);
  if (!isValidationField(key) || !v$.value[field]?.$error) return '';
  return t(`CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.${key}.ERROR`);
};

const validate = async () => {
  v$.value.$touch();
  return v$.value.$validate();
};

const handleCountrySelection = value => {
  const selectedCountry = countries.find(option => option.id === value);
  const changed = state.additionalAttributes.countryCode !== (value || '');
  state.additionalAttributes.countryCode = value || '';
  state.additionalAttributes.country = selectedCountry?.name || '';
  if (changed) {
    state.additionalAttributes.state = '';
    state.additionalAttributes.city = '';
  }
  emit('update', state);
};

const handleStateSelection = value => {
  if ((value || '') === state.additionalAttributes.state) return;
  state.additionalAttributes.state = value || '';
  state.additionalAttributes.city = '';
  emit('update', state);
};

const handleCitySelection = value => {
  state.additionalAttributes.city = value || '';
  emit('update', state);
};

const handleGenderSelection = value => {
  state.additionalAttributes.gender = value || '';
  emit('update', state);
};

const handleCustomAttributesUpdate = value => {
  state.customAttributes = value;
  const { firstName, lastName, ...stateWithoutNames } = state;
  emit('update', stateWithoutNames);
};

const handleContactAttributeUpdate = (attributeKey, value) => {
  handleCustomAttributesUpdate({
    ...state.customAttributes,
    [attributeKey]: value,
  });
};

const resetValidation = () => {
  v$.value.$reset();
};

const companiesStore = useCompaniesStore();
const companyOptions = ref([]);

const resetForm = () => {
  Object.assign(state, defaultState);
  companyOptions.value = [];
  selectedLabels.value = [];
};

const companiesFromStore = () => {
  companyOptions.value = companiesStore.getCompaniesList.map(c => ({
    value: c.id,
    label: c.name || String(c.id),
  }));
};

const loadCompanies = async () => {
  await companiesStore.get({ page: 1, sort: 'name' });
  companiesFromStore();
};

const searchCompanies = debounce(async query => {
  if (!query || query.length < 1) {
    await loadCompanies();
    return;
  }
  await companiesStore.search({ search: query, page: 1 });
  companiesFromStore();
}, 300);

const handleCompanySelect = value => {
  state.companyId = value;
  emit('update', state);
};

const fetchLabels = async id => {
  try {
    const { data } = await ContactAPI.getContactLabels(id);
    selectedLabels.value = data.payload || [];
  } catch {
    selectedLabels.value = [];
  }
};

const handleLabelsUpdate = async labels => {
  selectedLabels.value = labels;
  if (props.isNewContact || !state.id) return;
  try {
    await ContactAPI.updateContactLabels(state.id, labels);
  } catch {
    // silent, mirrors the pattern already used for label persistence in ContactsCard.vue
  }
};

watch(
  () => props.contactData?.id,
  id => {
    if (id) {
      prepareStateBasedOnProps();
      fetchLabels(id);
    }
  },
  { immediate: true }
);

const setCompanyId = companyId => {
  state.companyId = companyId;
  emit('update', state);
};

// Expose state to parent component for avatar upload
defineExpose({
  state,
  selectedLabels,
  resetValidation,
  isFormInvalid,
  resetForm,
  validate,
  companyOptions,
  searchCompanies,
  setCompanyId,
  getFormBinding,
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
      <template v-for="(field, index) in orderedFields" :key="field.id">
        <div
          v-if="field.showGroupHeader"
          class="sm:col-span-2"
          :class="{ 'mt-2': index > 0 }"
        >
          <FormSectionHeader :title="t(field.groupTitleKey)" uppercase>
            <template
              v-if="field.group === 'details' && !isNewContact"
              #actions
            >
              <ContactMergeModal :primary-contact="contactData">
                <template #trigger>
                  <Button
                    v-tooltip.top-end="t('CONTACT_PANEL.MERGE_CONTACT')"
                    icon="i-ph-arrows-merge"
                    slate
                    faded
                    sm
                    :disabled="uiFlags?.isMerging"
                  />
                </template>
              </ContactMergeModal>
            </template>
          </FormSectionHeader>
        </div>
        <div :class="{ 'sm:col-span-2': field.fullWidth }">
          <ContactFormLabels
            v-if="field.type === 'standard' && field.key === 'labels'"
            :selected-labels="selectedLabels"
            @update:selected-labels="handleLabelsUpdate"
          />
          <div
            v-else-if="field.type === 'standard' && field.key === 'country'"
            class="relative flex flex-col gap-1"
          >
            <FormFieldLabel
              :label="standardLabel(field.key)"
              :hint="hasPixelHint(field.key)"
            />
            <ComboBox
              :model-value="state.additionalAttributes.countryCode"
              :options="countryOptions"
              :placeholder="standardPlaceholder(field.key)"
              class="[&>div>button]:h-8"
              :class="{
                '[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:!outline-transparent':
                  !isDetailsView,
                '[&>div>button]:!bg-n-alpha-black2': isDetailsView,
              }"
              @update:model-value="handleCountrySelection"
            />
            <button
              v-if="state.additionalAttributes.countryCode"
              type="button"
              class="absolute right-8 bottom-1 p-0.5 text-n-slate-9 hover:text-n-slate-12 transition-colors z-10"
              @click="handleCountrySelection('')"
            >
              <span class="i-lucide-x size-3.5" />
            </button>
          </div>
          <div
            v-else-if="
              field.type === 'standard' &&
              field.key === 'state' &&
              isStateSelect
            "
            class="flex flex-col gap-1"
          >
            <FormFieldLabel
              :label="standardLabel(field.key)"
              :hint="hasPixelHint(field.key)"
            />
            <ComboBox
              :model-value="state.additionalAttributes.state"
              :options="stateSelectOptions"
              :placeholder="statePlaceholder"
              :disabled="
                !state.additionalAttributes.countryCode || loadingStates
              "
              class="[&>div>button]:h-8"
              :class="{
                '[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:!outline-transparent':
                  !isDetailsView,
                '[&>div>button]:!bg-n-alpha-black2': isDetailsView,
              }"
              @update:model-value="handleStateSelection"
            />
          </div>
          <div
            v-else-if="
              field.type === 'standard' && field.key === 'city' && isCitySelect
            "
            class="flex flex-col gap-1"
          >
            <FormFieldLabel
              :label="standardLabel(field.key)"
              :hint="hasPixelHint(field.key)"
            />
            <ComboBox
              :model-value="state.additionalAttributes.city"
              :options="citySelectOptions"
              :placeholder="cityPlaceholder"
              :disabled="!state.additionalAttributes.state || loadingCities"
              class="[&>div>button]:h-8"
              :class="{
                '[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:!outline-transparent':
                  !isDetailsView,
                '[&>div>button]:!bg-n-alpha-black2': isDetailsView,
              }"
              @update:model-value="handleCitySelection"
            />
          </div>
          <div
            v-else-if="field.type === 'standard' && field.key === 'gender'"
            class="relative flex flex-col gap-1"
          >
            <FormFieldLabel
              :label="standardLabel(field.key)"
              :hint="hasPixelHint(field.key)"
            />
            <ComboBox
              v-model="state.additionalAttributes.gender"
              :options="genderOptions"
              :placeholder="standardPlaceholder(field.key)"
              class="[&>div>button]:h-8"
              :class="{
                '[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:!outline-transparent':
                  !isDetailsView,
                '[&>div>button]:!bg-n-alpha-black2': isDetailsView,
              }"
              @update:model-value="handleGenderSelection"
            />
            <button
              v-if="state.additionalAttributes.gender"
              type="button"
              class="absolute right-8 bottom-1 p-0.5 text-n-slate-9 hover:text-n-slate-12 transition-colors z-10"
              @click="handleGenderSelection('')"
            >
              <span class="i-lucide-x size-3.5" />
            </button>
          </div>
          <div
            v-else-if="
              field.type === 'standard' && field.key === 'phone_number'
            "
            class="flex flex-col gap-1"
          >
            <FormFieldLabel
              :label="standardLabel(field.key)"
              :hint="hasPixelHint(field.key)"
            />
            <PhoneNumberInput
              v-model="getFormBinding('PHONE_NUMBER').value"
              :placeholder="standardPlaceholder(field.key)"
              :show-border="isDetailsView"
            />
          </div>
          <div
            v-else-if="
              field.type === 'standard' && field.key === 'social_profiles'
            "
            class="flex flex-wrap gap-2"
          >
            <div
              v-for="item in socialProfilesForm"
              :key="item.key"
              class="flex items-center h-8 gap-2 px-2 rounded-lg"
              :class="{
                'bg-n-alpha-2 dark:bg-n-solid-2': isDetailsView,
                'bg-n-alpha-2 dark:bg-n-solid-3': !isDetailsView,
              }"
            >
              <Icon
                :icon="item.icon"
                class="flex-shrink-0 text-n-slate-11 size-4"
              />
              <input
                v-model="
                  state.additionalAttributes.socialProfiles[
                    item.key.toLowerCase()
                  ]
                "
                class="w-auto min-w-[100px] text-sm bg-transparent outline-none reset-base text-n-slate-12 dark:text-n-slate-12 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10"
                :placeholder="item.placeholder"
                :size="item.placeholder.length"
                @input="emit('update', state)"
              />
            </div>
          </div>
          <div
            v-else-if="field.type === 'standard'"
            class="flex flex-col gap-1"
          >
            <FormFieldLabel
              :label="standardLabel(field.key)"
              :for-id="`contact-${field.key}`"
              :hint="hasPixelHint(field.key)"
            />
            <Input
              :id="`contact-${field.key}`"
              v-model="getFormBinding(STANDARD_TO_CONFIG[field.key]).value"
              :type="field.key === 'birth_date' ? 'date' : 'text'"
              :placeholder="standardPlaceholder(field.key)"
              :message="getErrorMessage(STANDARD_TO_CONFIG[field.key])"
              :message-type="getMessageType(STANDARD_TO_CONFIG[field.key])"
              :custom-input-class="`h-8 !pt-1 !pb-1 ${
                !isDetailsView
                  ? '[&:not(.error,.focus)]:!outline-transparent'
                  : ''
              }`"
              class="w-full"
              @input="
                isValidationField(STANDARD_TO_CONFIG[field.key]) &&
                  v$[getValidationKey(STANDARD_TO_CONFIG[field.key])].$touch()
              "
              @blur="
                isValidationField(STANDARD_TO_CONFIG[field.key]) &&
                  v$[getValidationKey(STANDARD_TO_CONFIG[field.key])].$touch()
              "
            />
          </div>
          <ContactCustomAttributeItem
            v-else-if="field.type === 'custom'"
            :attribute="customAttributeWithValue(field.attribute)"
            @update="handleContactAttributeUpdate"
          />
        </div>
      </template>
      <div v-if="isNewContact" class="flex flex-col gap-1">
        <span class="mb-0.5 text-heading-3 text-n-slate-12">{{
          t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.COMPANY_LINK.LABEL')
        }}</span>
        <ComboBox
          v-model="state.companyId"
          :options="companyOptions"
          :placeholder="
            t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.COMPANY_LINK.PLACEHOLDER')
          "
          :search-placeholder="
            t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.COMPANY_LINK.PLACEHOLDER')
          "
          :empty-state="
            t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.COMPANY_LINK.NO_RESULTS')
          "
          @search="searchCompanies"
          @update:model-value="handleCompanySelect"
        />
        <slot name="company-link-action" />
      </div>
    </div>
  </div>
</template>
